import gql from 'graphql-tag';
import {
  Component,
  ProvideReactive,
  Mixins,
  Prop,
} from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import PimcoreIntrospectionMixin from '@/mixins/PimcoreIntrospectionMixin';
import NormalizerMixin from '@/mixins/NormalizerMixin';
import EntityHelperMixin from '@/mixins/EntityHelperMixin';
import GraphQLFieldsExtractor from '@/classes/graphql-introspection/GraphQLFieldsExtractor';
import SystemFieldsExcluder from '@/classes/graphql-introspection/PimcoreSystemFieldsExcluder';
import EntitySchemaConstructor from '@/classes/schema-constructors/EntitySchemaConstructor';
import {
  LayoutFieldType,
  PimcoreEntityLayout,
  PimcoreLayoutInput,
  PimcoreLayoutPanel,
  PimcoreQueryListingResponse,
} from '@/types/entity';
import TypeInspector from '@/classes/graphql-introspection/TypeInspector';
import FilterBuilder, { FilterBuilderInterface } from '@/classes/pimcore-filtering/FilterBuilder';
import { and } from '@/classes/pimcore-filtering/Filters';

import { TypeExtractionRules } from '@/classes/schema-constructors/types';

@Component
export default class EntityHandler extends Mixins(PimcoreIntrospectionMixin, NormalizerMixin, EntityHelperMixin) {
  // Array of predefined filters
  @Prop({
    type: Array,
    required: false,
  }) readonly predefinedFilters!: any

  @Prop({
    type: Object,
    default: null,
  }) readonly customByObject!: any

  entityListingData: PimcoreQueryListingResponse = {
    items: [],
    total: 0,
  };

  fieldsExcluder = new SystemFieldsExcluder();

  fieldsExtractor = new GraphQLFieldsExtractor(this.fieldsExcluder);

  entitySchemaConstructor: EntitySchemaConstructor | null = null;

  fixedEntity = '';

  fixedFirst = 0;

  get entity(): string {
    return this.fixedEntity || this.$route.params.entityName;
  }

  get currentEntityList() {
    return this.pimcoreEntityIntrospection?.entityListingQuery;
  }

  get entityMutations() {
    return this.pimcoreEntityIntrospection?.entityMutations || [];
  }

  get supportedCrudActions() {
    const entries = this.entityMutations.map((em) => [em.name.replace(this.entity, ''), true]);
    return Object.fromEntries(entries);
  }

  get gridSupportedHeaders(): Record<string, any>[] {
    return this.queryFields
      .filter((field) => {
        const { type } = field;
        return TypeInspector.isScalarType(type)
          || TypeInspector.isObjectType(type)
          || TypeInspector.isUnionType(type);
      });
  }

  get gridSupportedHeadersTitle(): string[] {
    return this.gridSupportedHeaders.map(({ name }) => name);
  }

  get gridHeaders() {
    if (this.isLayoutLoading) {
      return [];
    }
    const text = this.$t('Actions').toString();
    const actions = {
      text,
      value: 'actions',
      sortable: false,
      width: null,
    };

    const headers = this.gridHeadersLayoutFields.length > 0
      ? this.gridHeadersLayoutFields
      : this.gridSupportedHeaders;

    let gridHeaders = headers
      .map((field) => {
        let width;
        if (field.style) {
          const match = field.style.match('(?!width: ?)?[0-9].*(?=px;)');
          width = match?.[0];
        }
        return {
          text: field.title || this.$t(field.name),
          value: field.name,
          sortable: false,
          width,
        };
      });
    gridHeaders = [{
      text: 'ID',
      value: 'id',
      sortable: false,
      width: null,
    }, {
      text: this.$t('creationDate'),
      value: 'creationDate',
      sortable: false,
      width: null,
    }, ...gridHeaders];
    const hasAnyCrudActions = Object.keys(this.supportedCrudActions).length;
    if (hasAnyCrudActions) gridHeaders.push(actions);
    return gridHeaders;
  }

  /**
   * Заголовки таблицы из макета грида
   */
  get gridHeadersLayoutFields(): PimcoreLayoutInput[] {
    const childs = this.gridLayout(this.entity);
    if (childs && childs.length > 0) {
      return childs
        .filter((field: PimcoreLayoutPanel | PimcoreLayoutInput) => {
          const isSupported = this.gridSupportedHeadersTitle.includes(field.name);
          const isFieldInput = field.fieldtype !== LayoutFieldType.panel;
          return isSupported && isFieldInput;
        }) as PimcoreLayoutInput[];
    }
    return [];
  }

  get gridItems() {
    if (!this.entityListingData || !this.entityListingData.items) {
      return [];
    }
    const flattedItems = this.entityListingData?.items.map((item: any) => {
      const returnedItem = item || {};
      returnedItem.summaryQuantityDay = 0;
      returnedItem.summaryQuantityNight = 0;
      if (returnedItem.operationWorks) {
        returnedItem.operationWorks.forEach((work: any) => {
          if (work.time_interval === 'day') {
            returnedItem.summaryQuantityDay += work.quantity;
          } else if (work.time_interval === 'night') {
            returnedItem.summaryQuantityNight += work.quantity;
          }
        });
      }
      return returnedItem;
    });
    return flattedItems.map(this.performRowItem);
  }

  @Getter('isLayoutLoading', { namespace: 'entity' })
  isLayoutLoading!: boolean;

  /**
   * Хелпер получения макета pimcore по типу
   */
  @Getter('layoutByType', { namespace: 'entity' })
  layoutByType!: (entity: string, type: string) => PimcoreEntityLayout | null;

  /**
   * Макет pimcore вывода списка сущностей
   */
  @Getter('gridLayoutFields', { namespace: 'entity' })
  gridLayout!: (entity: string) => Array<PimcoreLayoutInput | PimcoreLayoutPanel>;

  @Action('fetchEntityLayouts', { namespace: 'entity' })
  fetchEntityLayouts: any;

  @ProvideReactive()
  get queryFields() {
    return this.pimcoreEntityIntrospection?.queryFields || [];
  }

  @ProvideReactive()
  get mutationFields() {
    return this.pimcoreEntityIntrospection?.mutationFields || [];
  }

  async beforeMount() {
    await this.init();
  }

  async init() {
    await this.initPimcoreIntrospection(this.entity);
    const searchObj: any = {
      first: this.fixedFirst || 10,
      after: 0,
    };

    const filter = this.generateFilters();
    if (filter) {
      searchObj.filter = filter;
    }
  }

  generateFilters(filterBuilder?: FilterBuilderInterface): string {
    const finalFilterBuilder = new FilterBuilder();

    const filters = [];
    if (this.predefinedFilters) {
      filters.push(...this.predefinedFilters);
    }
    if (filterBuilder && !filterBuilder.isEmpty()) {
      filters.push(...filterBuilder.filters);
    }

    if (!filters || !filters.length) {
      return '';
    }

    finalFilterBuilder.addFilters(and(...filters));

    if (finalFilterBuilder.isEmpty()) {
      return '';
    }
    return finalFilterBuilder.build();
  }

  performRowItem(item: any) {
    const entries = Object
      .entries(item)
      .map(([key, value]) => [key, this.normalizeValue(value)]);
    return Object.fromEntries(entries);
  }

  async fetchMoreEntityListingData(variables: Record<string, string | number | undefined>, rules: TypeExtractionRules = { maxDepth: 3 }) {
    if (!rules.byObject) {
      rules.byObject = this.customByObject;
    }
    variables.sortBy = 'o_id';
    variables.sortOrder = 'DESC';
    this.entityListingData = await this._loadEntityListing(this.entity, variables, 'network-only', rules);
  }
}
